import DOMPurify, { type Config, type RemovedAttribute, type RemovedElement } from 'dompurify'
import {
    ALLOWED_EMAIL_HTML_ATTRIBUTES,
    ALLOWED_EMAIL_HTML_TAGS,
    ALLOWED_EMAIL_LINK_PROTOCOLS,
    FORBIDDEN_EMAIL_HTML_ATTRIBUTES,
    FORBIDDEN_EMAIL_HTML_TAGS,
    inspectEmailCss,
    isDangerousEmailHtmlAttribute,
    isDangerousEmailHtmlTag,
    sanitizeEmailImageSource,
} from '@numeo/types-main-web-app'

export const MAX_TEMPLATE_HTML_CHARACTERS = 100_000

const LINK_PROTOCOLS = new Set(ALLOWED_EMAIL_LINK_PROTOCOLS)
// Tags the Visual editor would drop or reshape. Tables, images, rules, headings and quotes are not here: the
// editor keeps them as written (components/tiptap/extensions/PreservedHtml). Column definitions are — it lays out
// rows and cells only.
const VISUAL_EDITOR_UNSUPPORTED_TAGS = new Set(['caption', 'col', 'colgroup'])

interface TemplateHtmlInspection {
    sanitizedHtml: string
    issues: string[]
}

const SANITIZE_CONFIG: Config = {
    ALLOWED_TAGS: [...ALLOWED_EMAIL_HTML_TAGS],
    ALLOWED_ATTR: [...ALLOWED_EMAIL_HTML_ATTRIBUTES],
    ALLOW_ARIA_ATTR: true,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SANITIZE_DOM: true,
    SANITIZE_NAMED_PROPS: true,
    SAFE_FOR_XML: true,
    KEEP_CONTENT: true,
    FORBID_TAGS: [...FORBIDDEN_EMAIL_HTML_TAGS],
    FORBID_ATTR: [...FORBIDDEN_EMAIL_HTML_ATTRIBUTES],
}

const hasAllowedProtocol = (rawValue: string, protocols: ReadonlySet<string>): boolean => {
    try {
        return protocols.has(new URL(rawValue.trim()).protocol.toLowerCase())
    } catch {
        return false
    }
}

const addIssue = (issues: string[], issue: string): void => {
    if (!issues.includes(issue)) issues.push(issue)
}

const sanitizeStyle = (element: HTMLElement, issues: string[]): void => {
    const inspection = inspectEmailCss(element.getAttribute('style') ?? '')
    inspection.issues.forEach((issue) => addIssue(issues, issue))
    if (inspection.sanitizedStyle) element.setAttribute('style', inspection.sanitizedStyle)
    else element.removeAttribute('style')
}

const describeDangerousRemovedItem = (item: RemovedAttribute | RemovedElement): string | null => {
    if ('element' in item) {
        const tag = item.element.nodeName.toLowerCase()
        if (tag === '#comment' || ['body', 'html'].includes(tag) || !isDangerousEmailHtmlTag(tag)) return null
        return `HTML element <${tag}> is not allowed`
    }
    const attribute = item.attribute?.name ?? 'unknown'
    return isDangerousEmailHtmlAttribute(attribute) ? `HTML attribute "${attribute}" is not allowed` : null
}

export const inspectTemplateHtml = (rawHtml: string): TemplateHtmlInspection => {
    if (rawHtml.length > MAX_TEMPLATE_HTML_CHARACTERS) {
        return { sanitizedHtml: '', issues: ['Template HTML exceeds the 100,000-character limit'] }
    }

    const purified = DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG)
    const issues: string[] = []
    DOMPurify.removed.forEach((item) => {
        const issue = describeDangerousRemovedItem(item)
        if (issue) addIssue(issues, issue)
    })
    const document = new DOMParser().parseFromString(`<!doctype html><html><body>${purified}</body></html>`, 'text/html')

    document.body.querySelectorAll<HTMLElement>('[style]').forEach((element) => sanitizeStyle(element, issues))
    document.body.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
        if (!hasAllowedProtocol(link.getAttribute('href') ?? '', LINK_PROTOCOLS)) {
            addIssue(issues, 'Link URL protocol is not allowed')
            link.removeAttribute('href')
        }
        if (link.target && link.target !== '_blank' && link.target !== '_self') {
            addIssue(issues, `Link target "${link.target}" is not allowed`)
            link.removeAttribute('target')
        }
        if (link.target === '_blank') link.setAttribute('rel', 'noopener noreferrer')
    })
    document.body.querySelectorAll<HTMLImageElement>('img[src]').forEach((image) => {
        const safeSource = sanitizeEmailImageSource(image.getAttribute('src') ?? '')
        if (!safeSource) {
            addIssue(issues, 'Image URL protocol is not allowed')
            image.removeAttribute('src')
            return
        }
        image.setAttribute('src', safeSource)
    })

    return { sanitizedHtml: document.body.innerHTML, issues }
}

export const sanitizeTemplateHtml = (rawHtml: string): string => {
    const inspection = inspectTemplateHtml(rawHtml)
    if (inspection.issues.includes('Template HTML exceeds the 100,000-character limit')) throw new Error(inspection.issues[0])
    return inspection.sanitizedHtml
}

/** HTML tags written out as text — what a chatbot's copy button or a code editor puts on the clipboard. */
const MARKUP_AS_TEXT = /<\/?(?:table|tbody|thead|tfoot|tr|td|th|div|p|span|a|img|br|hr|h[1-6]|ul|ol|li|strong|b|em|i|u|center|font|html|head|body)\b[^>]*>/i

/** A chatbot answer that wraps the code in a Markdown fence, possibly with a sentence around it. */
const CODE_FENCE = /```[a-z]*[^\S\n]*\n([\s\S]*?)```/i

/**
 * The code in a chatbot answer: inside its fence when it has one. Otherwise — the prompt asks for no fence, and
 * a model that obeys still tends to add a sentence before and after — the run of lines from the first that opens
 * with a tag to the last that closes with one. Prose never starts and ends that way, so a sentence that merely
 * mentions "<p>", or one after the code saying "miles > 300", stays out. Empty when there is no such run.
 */
const codeFromAnswer = (text: string): string => {
    const fenced = text.match(CODE_FENCE)?.[1]
    if (fenced !== undefined) return fenced.trim()
    const lines = text.split('\n')
    const first = lines.findIndex((line) => line.trimStart().startsWith('<'))
    let last = lines.length - 1
    while (last >= 0 && !lines[last].trimEnd().endsWith('>')) last -= 1
    return first === -1 || last < first
        ? ''
        : lines
              .slice(first, last + 1)
              .join('\n')
              .trim()
}

/** Copied RENDERED content that is an email layout rather than a data grid. Email layouts nest tables or mark
 *  them presentational; spreadsheet cells and data tables copied from a page — even several side by side —
 *  are neither. Parsed rather than pattern-matched, so "nested" means a table inside a table. */
const isRenderedEmailLayout = (html: string): boolean => {
    if (!/<table\b/i.test(html)) return false
    const document = new DOMParser().parseFromString(html, 'text/html')
    return document.querySelector('table table, table[role="presentation"]') !== null
}

/**
 * The email design in a paste, or null when there is none. Either HTML copied as CODE — a chatbot's answer
 * (its words stripped) or a code editor — or copied RENDERED content laid out as an email. Ordinary rich text,
 * and cells copied from a spreadsheet, are not designs: the Visual editor takes them as they are.
 */
export const extractPastedDesign = (plainText: string, html: string): string | null => {
    const text = plainText.trim()
    if (MARKUP_AS_TEXT.test(text)) return codeFromAnswer(text) || null
    if (isRenderedEmailLayout(html)) return html
    return null
}

/** Whether sanitized email HTML would show the broker anything — words, a {{variable}} or an image — rather than
 *  only empty elements. */
export const hasEmailContent = (html: string): boolean => {
    const document = new DOMParser().parseFromString(html, 'text/html')
    return (document.body.textContent ?? '').trim().length > 0 || document.body.querySelector('img') !== null
}

export const findVisualEditorUnsupportedElements = (html: string): string[] => {
    const document = new DOMParser().parseFromString(`<!doctype html><html><body>${html}</body></html>`, 'text/html')
    const unsupported = new Set<string>()
    document.body.querySelectorAll('*').forEach((element) => {
        const tag = element.tagName.toLowerCase()
        if (VISUAL_EDITOR_UNSUPPORTED_TAGS.has(tag)) unsupported.add(tag)
    })
    return [...unsupported].sort()
}
